import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Picker } from '@/app/components/Picker'
import type { PickerItem } from '@/app/components/Picker'
import { useGenerationFormStore } from '../store'
import { useAvailableModels } from '../hooks'
import { RESOURCE_TYPE } from '../types'
import { IconTrash, IconPlus } from '@/app/components/icons'

interface LoraItem extends PickerItem {
  readonly fileName: string
}

function parseLoraName(fullPath: string): LoraItem {
  const parts = fullPath.split('/')
  const fileName = parts[parts.length - 1] ?? fullPath
  const baseName = fileName.replace(/\.(safetensors|ckpt|pt|bin)$/i, '')
  const folder = parts.length > 1 ? parts.slice(0, -1).join('/') : undefined

  return {
    id: fullPath,
    label: baseName,
    description: folder,
    fileName,
  }
}

export function LoraList() {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const loras = useGenerationFormStore((s) => s.loras)
  const addLora = useGenerationFormStore((s) => s.addLora)
  const removeLora = useGenerationFormStore((s) => s.removeLora)
  const updateLoraWeight = useGenerationFormStore((s) => s.updateLoraWeight)
  const isGenerating = useGenerationFormStore((s) => s.isGenerating)

  const { data: availableLoras, isLoading } = useAvailableModels(RESOURCE_TYPE.LORA)

  const items = useMemo(
    () => (availableLoras ?? []).map(parseLoraName),
    [availableLoras],
  )

  // Exclude already-added loras from the picker
  const addedIds = useMemo(
    () => new Set(loras.map((l) => l.resource_id)),
    [loras],
  )

  const availableItems = useMemo(
    () => items.filter((item) => !addedIds.has(item.id)),
    [items, addedIds],
  )

  const handleSelect = useCallback(
    (item: LoraItem) => {
      addLora({ resource_id: item.id, weight: 1.0 })
    },
    [addLora],
  )

  // Find display name for a resource_id
  const getDisplayName = useCallback(
    (resourceId: string): string => {
      const item = items.find((i) => i.id === resourceId)
      return item?.label ?? resourceId
    },
    [items],
  )

  return (
    <div className="flex flex-col gap-2">
      <label
        className="text-xs font-medium text-tg-hint"
      >
        {t('generate.loras')}
      </label>

      {/* Added LoRAs */}
      {loras.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {loras.map((lora, index) => (
            <div
              key={`${lora.resource_id}-${String(index)}`}
              className="flex items-center gap-2 rounded-lg border border-tg-hint/20 px-3 py-2 bg-tg-secondary"
            >
              <span
                className="min-w-0 flex-1 truncate text-xs text-tg-text"
              >
                {getDisplayName(lora.resource_id)}
              </span>
              <input
                type="number"
                value={lora.weight}
                onChange={(e) => { updateLoraWeight(index, Number(e.target.value)) }}
                min={-2}
                max={2}
                step={0.1}
                disabled={isGenerating}
                className="w-16 rounded border border-tg-hint/20 px-1.5 py-0.5 text-center text-xs outline-none input-focus bg-tg-bg text-tg-text"
              />
              <button
                type="button"
                onClick={() => { removeLora(index) }}
                disabled={isGenerating}
                className="rounded-md p-1 text-tg-destructive transition-all duration-150 hover:bg-tg-destructive/10 active:scale-90 disabled:opacity-50"
              >
                <IconTrash className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add LoRA button */}
      <button
        type="button"
        onClick={() => { setIsOpen(true) }}
        disabled={isGenerating || isLoading || availableItems.length === 0}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-tg-hint/30 px-3 py-2.5 text-sm font-medium transition-all duration-150 hover:border-tg-hint/50 hover:bg-tg-secondary active:scale-[0.98] disabled:opacity-50 text-tg-button"
      >
        <IconPlus className="h-4 w-4" />
        <span>
          {isLoading ? t('common.loading') : t('generate.addLora')}
        </span>
      </button>

      {/* Full-screen Picker */}
      <Picker<LoraItem>
        isOpen={isOpen}
        onClose={() => { setIsOpen(false) }}
        onSelect={handleSelect}
        items={availableItems}
        title={t('generate.loras')}
        searchPlaceholder={t('generate.searchLora')}
        isLoading={isLoading}
      />
    </div>
  )
}
